import { types } from "mobx-state-tree";
import { translationService } from "../translation/TranslationService";
import { nullableString } from "../../models/mstNullable";

const MainInfoEntry = types.model({
  key: nullableString(),
  value: nullableString(),
});

 const MainInfoStorageModel = types.model("MainInfoStorageModel", {
  // MainInformation.Key/Value are nullable strings on the API side. The payload reaches this
  // model via OcrService (types.frozen, which does not validate) -> setMainInformation, so this
  // is the first place a null would be type-checked.
  mainInformation: types.optional(types.array(MainInfoEntry), []),

  // What OCR actually extracted, kept apart from what is on screen.
  //
  // `mainInformation` is the DISPLAYED set: the reviewer's corrections overlaid on the machine
  // values. Telling the reader which of those they changed needs the original still available to
  // compare against, and it cannot be re-read from `mainInformation` once an edit has replaced
  // it — nor from OcrService, whose copy the save path overwrites in place. So the machine set is
  // captured once per file and then left alone.
  machineMainInformation: types.optional(types.array(MainInfoEntry), []),

  /**
   * Which set the table shows: the machine's extraction, the reviewer's, or both together.
   *
   * The same distinction the detection list draws, for the same reason — a merged table cannot
   * say which values a human decided, and the Edit button sitting above one made it look as
   * though the extraction itself were editable. It is not: corrections live in the review.
   */
  source: types.optional(types.string, "approved"),
}).views((self) => ({
  /** The set the current source is asking for. */
  get displayedMainInformation() {
    return self.source === "original" ? self.machineMainInformation : self.mainInformation;
  },

  /** One row per field, with both values, for the compare view. */
  get compareRows() {
    const machine = new Map(
      self.machineMainInformation.map((m) => [m.key ?? "", m.value ?? ""])
    );
    const approved = new Map(self.mainInformation.map((m) => [m.key ?? "", m.value ?? ""]));

    // Union, machine order first: a field the reviewer added has no machine row to sit under,
    // and dropping it would hide the very thing the compare view is for.
    // Array.from rather than spreading the iterators: this library builds to an ES5 target,
    // where spreading a Map iterator does not compile.
    const machineKeys = Array.from(machine.keys());
    const addedKeys = Array.from(approved.keys()).filter((k) => !machine.has(k));
    const keys = machineKeys.concat(addedKeys).filter((k) => k !== "");

    return keys.map((key, index) => ({
      id: index,
      field: key,
      ocr: machine.get(key) ?? "—",
      approved: approved.get(key) ?? "—",
    }));
  },

  get rows(){
    if (self.source === "compare") return (this as any).compareRows;
    const changed =
      self.source === "approved" ? ((this as any).reviewerChangedKeys as Set<string>) : new Set<string>();

    // Appended to the text rather than rendered as a chip or a colour. These cells are
    // pre-formatted "key : value" strings feeding a headerless DataGrid, and the table is
    // captured into the PDF as-is — a text marker is the only form that survives both that
    // capture and a greyscale print.
    const label = (entry: { key: string | null; value: string | null }) => {
      const base = `${entry.key} : ${entry.value}`;
      return changed.has(entry.key ?? "")
        ? `${base}  (${translationService.translate("comparison.main-info.edited-by-reviewer")})`
        : base;
    };

    const shown = (this as any).displayedMainInformation as typeof self.mainInformation;
    const rows = [];
    for (let i = 0; i < shown.length; i+= 2) {
      rows.push({
        id: i/2,
        column1: label(shown[i]),
        column2: shown[i + 1] ? label(shown[i + 1]) : '',
      })
    }
    return rows;
  },
  get columns() {
    if (self.source === "compare") {
      return [
        { field: 'field', headerName: translationService.translate("ocrService.mainInfo.parameters"), flex: 1, sortable: false, disableColumnMenu: true },
        { field: 'ocr', headerName: translationService.translate("comparison.main-info.column-ocr"), flex: 1, sortable: false, disableColumnMenu: true },
        { field: 'approved', headerName: translationService.translate("comparison.main-info.column-approved"), flex: 1, sortable: false, disableColumnMenu: true },
      ];
    }

    return [
      { field: 'column1', headerName: translationService.translate("ocrService.mainInfo.parameters"), flex: 1, sortable: false, disableColumnMenu: true },
      { field: 'column2', headerName: translationService.translate("ocrService.mainInfo.details"), flex: 1, sortable: false, disableColumnMenu: true },
    ];
  },

  /**
   * Keys whose displayed value is not what OCR read — the reviewer either corrected the value or
   * supplied a field the extraction never produced. Both count as "a human decided this", which
   * is the distinction a reader of the approved record needs.
   *
   * Empty while nothing has been reviewed, so an untouched file shows no markers at all.
   */
  get reviewerChangedKeys(): Set<string> {
    const machine = new Map(
      self.machineMainInformation.map((m) => [m.key ?? "", m.value ?? ""])
    );

    const changed = new Set<string>();
    self.mainInformation.forEach((m) => {
      const key = m.key ?? "";
      if (key === "") return;
      const before = machine.get(key);
      // An absent key is an addition; a different value is a correction. A key the machine had
      // and the reviewer left alone is neither.
      if (before === undefined || before !== (m.value ?? "")) changed.add(key);
    });

    return changed;
  },
}))
.actions((self) => ({
  setMainInformation(data: { key: string; value: string }[]) {
    self.mainInformation.replace(data);
  },

  setSource(source: string) {
    self.source = source;
  },

  /**
   * Record the machine's own extraction. Called once when a file's OCR data lands, BEFORE any
   * review overlay, and never from an edit — the whole point is that it does not move.
   */
  setMachineMainInformation(data: { key: string; value: string }[]) {
    self.machineMainInformation.replace(data);
  },

  /**
   * Lay the reviewer's saved values over the machine's, by key.
   *
   * The review wins where it has an opinion, which is what makes a correction survive a page
   * reload — OcrService re-reads ocr.json on every load, so without this the screen would snap
   * back to the extracted value while the review quietly kept the corrected one. Keys only the
   * review has are appended: a reviewer supplying a field OCR never found is the normal case on
   * a run with a poor header.
   */
  applyReviewMainInformation(review: { key: string; value: string }[]) {
    if (!review || review.length === 0) return;

    const merged = self.machineMainInformation.map((m) => ({
      key: m.key ?? "",
      value: m.value ?? "",
    }));
    const indexByKey = new Map(merged.map((m, index) => [m.key, index]));

    review.forEach((r) => {
      const key = r.key ?? "";
      if (key === "") return;
      const existing = indexByKey.get(key);
      if (existing === undefined) merged.push({ key, value: r.value ?? "" });
      else merged[existing] = { key, value: r.value ?? "" };
    });

    self.mainInformation.replace(merged);
  },

  reset() {
    self.mainInformation.clear();
    self.machineMainInformation.clear();
  }
}))

export const mainInfoStorage = MainInfoStorageModel.create({
  mainInformation: [],
  machineMainInformation: []
});
